import { useGetAllAccounts } from '@/api/generated/accounts/accounts';
import { getGetAllGoalsQueryKey } from '@/api/generated/goals/goals';
import { useGetAllAssets } from '@/api/generated/market-data/market-data';
import {
  GOAL_PLANNING_WIZARD_STEPS,
  type GoalPlanningInterestId,
  type GoalPlanningWizardStep,
} from '@/components/goalPlanning/goalPlanningConstants';
import { persistOnboardingGoals } from '@/components/goalPlanning/goalPlanningPersist';
import { ConnectAccountsStep } from '@/components/goalPlanning/steps/ConnectAccountsStep';
import { FinancialInfoStep } from '@/components/goalPlanning/steps/FinancialInfoStep';
import { GoalCreationStep } from '@/components/goalPlanning/steps/GoalCreationStep';
import { InterestsStep } from '@/components/goalPlanning/steps/InterestsStep';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { useLanguage } from '@/i18n/language-hooks';
import {
  goalPlanningStorage,
  type FinancialInfo,
  type PlannedGoal,
} from '@/services/goalPlanningStorage';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

interface NewYearGoalPlanningDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete?: () => void;
}

export const NewYearGoalPlanningDialog = ({
  open,
  onOpenChange,
  onComplete,
}: NewYearGoalPlanningDialogProps) => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [currentStep, setCurrentStep] = useState<GoalPlanningWizardStep>('connect');
  const [financialInfo, setFinancialInfo] = useState<FinancialInfo | null>(
    goalPlanningStorage.getFinancialInfo()
  );
  const [goals, setGoals] = useState<PlannedGoal[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: assets, isLoading: isLoadingAssets } = useGetAllAssets();
  const { data: accountsResponse } = useGetAllAccounts();
  const accounts = accountsResponse?.items ?? [];

  const defaultCurrency =
    accounts.find((a) => a.type !== 'liability')?.currency ?? assets?.[0]?.code ?? 'USD';

  const currentStepIndex = GOAL_PLANNING_WIZARD_STEPS.indexOf(currentStep);
  const progress = ((currentStepIndex + 1) / GOAL_PLANNING_WIZARD_STEPS.length) * 100;

  const handleFinancialInfoSubmit = (info: FinancialInfo) => {
    setFinancialInfo(info);
    goalPlanningStorage.saveFinancialInfo(info);
    setCurrentStep('goals');
  };

  const handleGoalsSubmit = (createdGoals: PlannedGoal[]) => {
    setGoals(createdGoals);
    setCurrentStep('interests');
  };

  const handleInterestsSubmit = async (interests: GoalPlanningInterestId[]) => {
    if (goals.length === 0) return;

    const defaultAccountId = accounts.find((a) => a.type !== 'liability')?.id;
    setIsSubmitting(true);
    try {
      await persistOnboardingGoals({
        goals,
        interests,
        defaultAccountId,
        financialInfo,
      });
      await queryClient.invalidateQueries({ queryKey: getGetAllGoalsQueryKey() });
      onComplete?.();
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleBack = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(GOAL_PLANNING_WIZARD_STEPS[prevIndex]!);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='mx-4 flex max-h-[90vh] flex-col overflow-hidden sm:mx-0 sm:max-w-2xl'>
        <DialogHeader className='flex-shrink-0'>
          <DialogTitle>{t('goalPlanning.dialog.title')}</DialogTitle>
          <DialogDescription>{t('goalPlanning.dialog.description')}</DialogDescription>
          <div className='pt-2'>
            <div className='text-muted-foreground mb-1 flex items-center justify-between text-xs'>
              <span>
                {t('goalPlanning.dialog.step', {
                  current: currentStepIndex + 1,
                  total: GOAL_PLANNING_WIZARD_STEPS.length,
                })}
              </span>
              <span>{t(`goalPlanning.steps.${currentStep}.title`)}</span>
            </div>
            <Progress value={progress} className='h-2' />
          </div>
        </DialogHeader>

        <div className='min-h-0 flex-1 overflow-y-auto py-4 pr-1'>
          {currentStep === 'connect' && (
            <ConnectAccountsStep onContinue={() => setCurrentStep('financial')} />
          )}

          {currentStep === 'financial' && (
            <FinancialInfoStep
              initialData={financialInfo}
              assets={assets || []}
              isLoadingAssets={isLoadingAssets}
              onSubmit={handleFinancialInfoSubmit}
            />
          )}

          {currentStep === 'goals' && (
            <GoalCreationStep
              initialGoals={goals}
              currency={defaultCurrency}
              assets={assets || []}
              isLoadingAssets={isLoadingAssets}
              onSubmit={handleGoalsSubmit}
            />
          )}

          {currentStep === 'interests' && goals.length > 0 && (
            <InterestsStep
              initialInterests={goalPlanningStorage.getInterests() as GoalPlanningInterestId[]}
              onSubmit={handleInterestsSubmit}
              isSubmitting={isSubmitting}
            />
          )}
        </div>

        <div className='flex flex-shrink-0 items-center justify-between gap-2 border-t pt-4'>
          <div>
            {currentStepIndex > 0 && (
              <button
                type='button'
                onClick={handleBack}
                className='text-muted-foreground hover:text-foreground text-sm'
              >
                {t('common.back')}
              </button>
            )}
          </div>
          <div className='flex gap-2'>
            <button
              type='button'
              onClick={handleClose}
              className='text-muted-foreground hover:text-foreground px-3 py-1.5 text-sm'
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
